import { FolderOpenFilled } from '@ant-design/icons'
import { Tree } from 'antd'
import { useCallback, useEffect, useState } from 'react'

import { Button, Modal, Trans } from '@/components'

import * as fs from './fs'
import { IFile } from './fs'

export interface MoveDialogProps {
  onCancel: () => void
  onOk: () => void
  files: IFile[]
}

interface DataNode {
  title: string
  key: string
  file: IFile | undefined
  isLeaf?: boolean
  children?: DataNode[]
  icon?: React.ReactNode
}

const MoveDialog = ({ files, onCancel, onOk, domain }: MoveDialogProps & { domain: number }) => {
  const [loading, setLoading] = useState(false)
  const [treeData, setTreeData] = useState<DataNode[]>([])
  const [selectedDir, setSelectedDir] = useState<IFile | undefined>()

  const getDirections = useCallback(
    async (path: string): Promise<DataNode[]> => {
      return fs.ls({ path, domain }).then(({ files }) => {
        return files
          .filter((info: any) => info.isDir)
          .map((it: any) => {
            return {
              title: it.name,
              key: it.name,
              file: it,
              icon: <FolderOpenFilled style={{ color: 'orange', fontSize: 20 }} />,
            } as DataNode
          })
      })
    },
    [domain],
  )

  const makeNode = useCallback((parent: DataNode, children: DataNode[]) => {
    parent.children = children?.map((it) => {
      return {
        ...it,
        key: `${parent.key}${it.key}/`,
      }
    })
    return parent
  }, [])

  const findNode = useCallback((root: DataNode, key: string): DataNode | null => {
    if (root.key == key) {
      return root
    } else {
      const children = root.children || []
      for (let i = 0; i < children.length; i++) {
        const found = findNode(children[i], key)
        if (found) return found
      }
      return null
    }
  }, [])

  useEffect(() => {
    getDirections('/').then((treeData) => {
      const root = {
        title: '/',
        key: '/',
        file: {
          path: '/',
        },
        icon: <FolderOpenFilled style={{ color: 'orange', fontSize: 20 }} />,
      } as DataNode
      setTreeData([makeNode(root, treeData)])
    })
  }, [getDirections, makeNode])

  const onLoadData = useCallback(
    (item: DataNode) => {
      return new Promise<void>((resolve, reject) => {
        getDirections(item.key)
          .then((_treeData: DataNode[]) => {
            for (let i = 0; i < treeData.length; i++) {
              const found = findNode(treeData[i], item.key)
              if (found) {
                makeNode(found, _treeData)
                setTreeData([...treeData])
                break
              }
            }
            resolve()
          })
          .catch(reject)
      })
    },
    [getDirections, findNode, makeNode, treeData],
  )

  const _onConfirm = useCallback(async () => {
    if (files.length > 0 && selectedDir) {
      const getTarget = (path: string, dirname: string) => {
        const parts = path.split('/')
        if (parts.reverse()[0] == '') {
          return `${dirname}${parts[1]}/`
        } else {
          return `${dirname}${parts[0]}`
        }
      }
      setLoading(true)
      try {
        await Promise.all(
          files.map((file) =>
            fs.mv({
              path: file.path,
              target: getTarget(file.path, selectedDir.path),
              domain,
            }),
          ),
        )
        setSelectedDir(undefined)
        onOk()
      } catch (error) {
        setLoading(false)
      }
    }
  }, [files, selectedDir, onOk, domain])

  return (
    <Modal open={files.length > 0} onClose={onCancel}>
      <Modal.Header>Move</Modal.Header>
      <Modal.Body>
        <div style={{ maxHeight: window.innerHeight * 0.7, overflowY: 'auto' }}>
          {treeData.length ? (
            <Tree
              multiple={false}
              onSelect={(_: React.Key[], { node }) => {
                setSelectedDir(node.file)
              }}
              defaultExpandedKeys={['/']}
              showIcon
              loadData={onLoadData}
              treeData={treeData}
            />
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onCancel}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button type="secondary" onClick={_onConfirm} disabled={loading || !selectedDir}>
          {loading ? (
            <Trans i18nKey="common.confirmModalOkLoading">Processing...</Trans>
          ) : (
            <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export function useMove(domain: number) {
  return {
    MoveDialog: useCallback(
      (props: MoveDialogProps) => <MoveDialog {...props} domain={domain} />,
      [domain],
    ),
  }
}
